import { Box, useMediaQuery, useTheme } from '@ublend-npm/aulaui-core';
import React, { useEffect, useState } from 'react';
import AssignmentSidebar from './AssignmentSidebar/AssignmentSidebar';
import { createStyles } from './AssignmentResponsive.styles';
import {
  getBackButtonRoute,
  goToMySpaceNav,
} from '@core/constants/backButtonNavigation';
import NavigationBackHeader from '../../common/NavigationBackHeader/NavigationBackHeader';
import { getCurrentSpaceId } from '@core/selectors/space';
import { useSelector } from 'react-redux';
import { AccessTokenState, AulaState, Space, User } from '@core/types/state';
import {
  getAssignments,
  selectCurrentAssignmentId,
} from '@core/assignments/selectors';
import pushRoute from '@app/utils/routing';
import AssignmentDetails from './AssignmentDetails/AssignmentDetails';
import { ResponsiveAssignment } from '@core/assignments/types';
import MaterialPreviewModal from './MaterialPreviewModal/MaterialPreviewModal';

type Props = {
  accessTokens: AccessTokenState;
  params?: {
    assignmentId?: string;
  };
  fetchAccessToken: ({
    spaceId,
  }: {
    spaceId: string;
  }) => Promise<string | null>;
  fetchAssignments: (params: {
    user: User | null;
    accessToken: string;
    spaceId: string;
  }) => void;
  isFetching: boolean;
  isEducator?: boolean;
  baseDomain: string;
  needsToRefreshAccessTokenProp: () => boolean;
  selectedSpace?: Space;
  user: User | null;
};

const getBackRoute = (spaceId) => {
  if (spaceId) {
    const backButtonRoute = getBackButtonRoute();
    if (backButtonRoute) {
      return backButtonRoute;
    }
    return goToMySpaceNav(spaceId);
  }

  return null;
};

const AssignmentsResponsive = ({
  accessTokens,
  params,
  fetchAccessToken,
  fetchAssignments,
  isFetching,
  isEducator = false,
  baseDomain,
  needsToRefreshAccessTokenProp,
  selectedSpace,
  user,
}: Props) => {
  const theme = useTheme();
  const styles = createStyles(theme);
  const isMdUp = useMediaQuery(theme.breakpoints.up('md'));
  const state = useSelector((state: AulaState) => state);
  const spaceId = getCurrentSpaceId(state);
  const assignments = getAssignments(state);
  const currentAssignmentId = useSelector(selectCurrentAssignmentId);
  const routeAssignmentId = params?.assignmentId;
  const backRoute = getBackRoute(currentAssignmentId);
  const baseRoute = `/dashboard/${spaceId}/journey/assignments`;
  const mobileBackRoute = `${baseRoute}/list`;
  const [previewState, setPreviewState] = useState<{
    open: boolean;
    materialId: string | null;
    materialTitle: string;
  }>({
    open: false,
    materialId: null,
    materialTitle: '',
  });
  const handleFetchAccessToken = async (spaceId: string) => {
    if (needsToRefreshAccessTokenProp()) {
      return fetchAccessToken({ spaceId });
    }

    return accessTokens[spaceId]?.token || fetchAccessToken({ spaceId });
  };

  const handleFetchAssignments = async () => {
    const selectedSpaceId = selectedSpace?.objectId;

    if (isFetching || !selectedSpaceId) {
      return;
    }

    const accessToken = await handleFetchAccessToken(selectedSpaceId);

    if (accessToken) {
      fetchAssignments({
        user,
        accessToken,
        spaceId: selectedSpaceId,
      });
    }
  };

  useEffect(() => {
    handleFetchAssignments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSpace?.objectId]);

  const openMaterialPreview = (materialId: string, materialTitle?: string) => {
    if (!selectedSpace?.objectId) return;

    setPreviewState({
      open: true,
      materialId,
      materialTitle: materialTitle || '',
    });
  };

  const closeMaterialPreview = () => {
    setPreviewState({
      open: false,
      materialId: null,
      materialTitle: '',
    });
  };

  const [defaultAssignmentId, setDefaultAssignmentId] = useState<string>('');

  const firstAssignment = assignments[0];
  useEffect(() => {
    if (firstAssignment) {
      setDefaultAssignmentId(
        firstAssignment?.id ||
          firstAssignment?.objectId ||
          firstAssignment?.externalId
      );
    }
  }, [firstAssignment]);

  useEffect(() => {
    // Use the ID from the matched route rather than the Redux routing state.
    // Redux updates before this component unmounts when navigating away, which
    // can briefly clear currentAssignmentId and otherwise push users back here.
    if (routeAssignmentId) {
      return;
    }

    if (!defaultAssignmentId) {
      return;
    }

    pushRoute(`${baseRoute}/${defaultAssignmentId}?from=myspacenav`);
  }, [baseRoute, routeAssignmentId, defaultAssignmentId]);

  useEffect(() => {
    if (isMdUp && currentAssignmentId == 'list' && defaultAssignmentId) {
      pushRoute(`${baseRoute}/${defaultAssignmentId}`);
    }
  }, [baseRoute, currentAssignmentId, defaultAssignmentId, isMdUp]);

  const getContent = () => {
    if (isMdUp) {
      return (
        <Box sx={styles.containerWrapper}>
          <Box sx={styles.leftWrapper}>
            <AssignmentSidebar
              assignments={assignments as ResponsiveAssignment[]}
              backRoute={backRoute}
              spaceId={spaceId}
              baseDomain={baseDomain}
              isEducator={isEducator}
              onMaterialClick={openMaterialPreview}
              isDesktop={isMdUp}
            />
          </Box>
          <Box sx={{ flex: '1', padding: '40px 1.5rem 1rem' }}>
            <AssignmentDetails />
          </Box>
        </Box>
      );
    } else {
      return (
        <Box sx={styles.pageWrapper}>
          <Box sx={styles.buttonWrapper}>
            <NavigationBackHeader route={mobileBackRoute} />
          </Box>
          <Box sx={styles.pageContent}>
            <AssignmentDetails />
          </Box>
        </Box>
      );
    }
  };

  return (
    <>
      {currentAssignmentId == 'list' ? (
        <Box sx={{ height: '100%' }}>
          <AssignmentSidebar
            assignments={assignments as ResponsiveAssignment[]}
            backRoute={backRoute}
            spaceId={spaceId}
            baseDomain={baseDomain}
            isEducator={isEducator}
            onMaterialClick={openMaterialPreview}
            isDesktop={isMdUp}
          />
        </Box>
      ) : (
        <Box>{getContent()}</Box>
      )}
      <MaterialPreviewModal
        open={previewState.open}
        onClose={closeMaterialPreview}
        onGoToPage={() => {
          if (previewState.materialId) {
            pushRoute(
              `/dashboard/${spaceId}/journey/materials/${previewState.materialId}?from=assignments`
            );
          }
        }}
        spaceId={selectedSpace?.objectId}
        materialId={previewState.materialId}
        previewTitle={previewState.materialTitle}
      />
    </>
  );
};

export default AssignmentsResponsive;
